import React from "react";
import styled from "styled-components";
import { ActionPlanRiskConsequence, ActionPlanRiskLikelihood, ActionPlanTopic, ActionPlanView, ActionPointData, PublicActionPlanLink, fieldValidation } from "../../../amorg-common";
import { deleteActionPlanTopic, updateActionPlanTopic, updateActionPlanTopicRisk } from "../../../api/actionPlanClientApi";
import { LoadingPageOverlay } from "../../../designSystem/LoadingPageOverlay";
import { TextAvatar } from '../../../designSystem/avatar/TextAvatar';
import { Button } from "../../../designSystem/button/Button";
import { DeleteConfirmationModal } from "../../../designSystem/compositeComponents/DeleteConfirmationButton";
import { EditableTextArea } from "../../../designSystem/compositeComponents/EditableTextArea";
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { amorgStyles } from "../../../designSystem/styles/amorgStyles";
import { H3 } from "../../../designSystem/typography/Heading";
import { AnimatedCollapsibleContent } from "../../../designSystem/collapsible/AnimatedCollapsibleContent";
import { ActionPlanTopicContextPanel } from "./ActionPlanTopicContextPanel";
import { RiskAssessmentEvaluator } from "../../../designSystem/graphs/RiskAssessmentEvaluator";
import { ActionPlanLinkEditor } from "./ActionPlanLinkEditor";
import { ActionPointListPanel } from "../../_components/actionpoints/ActionPointList";

type ActionPlanTopicListProps = {
    siteId:string,
    actionPlan: ActionPlanView,
    links: PublicActionPlanLink[],
    onUpdated: () => void;
};
export function ActionPlanTopicList (props: ActionPlanTopicListProps): JSX.Element
{
    return (
        <>
            {props.actionPlan.topics.length > 0 &&
                <Flex flexDirection="column" gap={4}>
                    {props.actionPlan.topics.map((t, i) =>
                    {
                        return (

                            <ActionPlanTopicView key={t.id}
                                siteId={props.siteId}
                                activityId={props.actionPlan.activityId}
                                actionPlanId={props.actionPlan.id}
                                topicIndex={i}
                                topic={t}
                                links={props.links}
                                onUpdated={props.onUpdated} />
                        );
                    })}
                </Flex>
            }
        </>
    );
}

type ActionPlanTopicViewProps =
    {
        siteId:string,
        activityId:string,
        actionPlanId:string,
        topicIndex: number,
        topic: ActionPlanTopic,
        links: PublicActionPlanLink[],
        onUpdated: () => void,
    };
function ActionPlanTopicView (props: ActionPlanTopicViewProps): JSX.Element
{
    const [isOpen, setIsOpen] = React.useState(false);
    const [inEditActionPoint, setInEditActionPoint] = React.useState<ActionPointData | null>(null);
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    // Refresh inEditActionPoint actionPoints are updated
    React.useEffect(() =>
    {
        if (inEditActionPoint)
        {
            const selected = props.topic.actionPoints.find(ap => ap.id === inEditActionPoint.id) || null;
            setInEditActionPoint(selected);
        }
        setIsOpen(true);
    }, [props.topic.actionPoints]);

    async function handleSaveDescription (value: string)
    {
        if ((await updateActionPlanTopic(props.topic.id, value)).isSuccess)
            props.onUpdated();
    }

    async function handleClickDeleteTopic ()
    {
        if ((await deleteActionPlanTopic(props.topic.id)).isSuccess)
        {
            props.onUpdated();
        }
        setShowDeleteModal(false);
    }

    async function handleUpdateTopicRisk (consequence: ActionPlanRiskConsequence, likelihood: ActionPlanRiskLikelihood)
    {
        setLoading(true);
        await updateActionPlanTopicRisk(props.topic.id, consequence, likelihood);
        setLoading(false);
        props.onUpdated();
    }

    return (
        <section>
            <LoadingPageOverlay loading={loading} />

            <StyledTopicContainer>

                <StyledTopicCol1>
                    <Flex flexDirection="column" justifyContent="center" style={{height:"100%"}}>
                        <Flex alignItems="center">
                            <div style={{width:'40px'}}>
                                <TextAvatar text={`${props.topicIndex + 1}`} size="32px" />
                            </div>
                            <div style={{flexGrow:1}}>
                                {props.topic.type === 'Basic' &&
                                    <EditableTextArea
                                        value={props.topic.description}
                                        validator={(currentValue) =>
                                        {
                                            return fieldValidation.actionPlanTopic.description(currentValue)
                                                ? { valid: true }
                                                : { valid: false, errorMessage: `Ugyldig beskrivelse. Skal udfyldes. Må ikke overstige 200 (${currentValue.length}) tegn.` };
                                        }}
                                        onSave={(value, valid) =>
                                        {
                                            if (valid)
                                                handleSaveDescription(value);
                                        }}
                                        ariaLabel={""}
                                        hideHoverEditButton
                                        placeholder={"Indtast emne"}
                                    />
                                }
                                <ActionPlanTopicContextPanel topic={props.topic} />
                            </div>  
                        </Flex>
                        
                        
                    </Flex>
                </StyledTopicCol1>
                
                <StyledTopicCol2>
                    <RiskAssessmentEvaluator
                        consequence={props.topic.risk.consequence}
                        likelihood={props.topic.risk.likelihood} 
                        onChange={handleUpdateTopicRisk}
                    />
                </StyledTopicCol2>

                <StyledTopicCol3>
                    <Button variant="destructive-text" icon="interface_actions_delete" onClick={() => setShowDeleteModal(true)} />
                    <ActionPlanLinkEditor
                        siteId={props.siteId}
                        actionPlanId={props.actionPlanId}
                        linkContext={{ type:'actionPlanTopic', topicId: props.topic.id }}
                        link={props.links.find(l => l.configuration.type === 'actionPlanTopic' && l.configuration.topicId === props.topic.id)}
                        onUpdated={props.onUpdated}  />
                </StyledTopicCol3>
                <StyledTopicCol4>
                    <Button variant="text" 
                        icon={isOpen ? 'arrows_chevron_chevronUp' : 'arrows_chevron_chevronDown'} 
                        onClick={() => setIsOpen(!isOpen)} />
                </StyledTopicCol4>
                    
            </StyledTopicContainer>

            <AnimatedCollapsibleContent open={isOpen}>
                <div style={{margin:'16px 36px 32px 64px'}}>
                    <H3 style={{margin:'8px 16px'}}>Handlepunkter</H3>
                    <ActionPointListPanel
                        mode={{ type: 'actionPlanTopic', topicId: props.topic.id }}
                        actionPoints={props.topic.actionPoints}
                        linkDisplay={{
                            siteId: props.siteId,
                            actionPlanId: props.actionPlanId,
                            links: props.links
                        }}
                        onUpdated={props.onUpdated} />
                </div>
            </AnimatedCollapsibleContent>

            <DeleteConfirmationModal
                showModal={showDeleteModal}
                confirmationHeading="Slet emne?"
                confirmationText='Er du sikker på du ønsker at slette dette emne?'
                requireKey={false}
                onConfirmDelete={handleClickDeleteTopic}
                buttonText="Slet emne"
                onCancel={() => setShowDeleteModal(false)}
            />

        </section>
    );
}

const StyledTopicContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 300px 100px 36px;
`;

const StyledTopicCol1 = styled.div`
    border:${props => amorgStyles(props.theme.colors).card.variants.light.default.border};
    border-top-left-radius: ${props => amorgStyles(props.theme.colors).card.borderRadius};
    border-top-left-radius: ${props => amorgStyles(props.theme.colors).card.borderRadius};
    padding: 8px 16px 8px 16px;
`;

const StyledTopicCol2 = styled.div`
    display: flex;
    align-items: fllex-start;
    justify-content: center;
    border-top:${props => amorgStyles(props.theme.colors).card.variants.light.default.border};
    border-bottom:${props => amorgStyles(props.theme.colors).card.variants.light.default.border};
    padding: 8px 16px 8px 16px;
`;

const StyledTopicCol3 = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    border:${props => amorgStyles(props.theme.colors).card.variants.light.default.border};
    border-top-right-radius: ${props => amorgStyles(props.theme.colors).card.borderRadius};
    border-bottom-right-radius: ${props => amorgStyles(props.theme.colors).card.borderRadius};
`;

const StyledTopicCol4 = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

